import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';
import { SearchBoxComponent } from '../../../shared/components/search-box/search-box.component';
import { SettingService } from '../../../Utils/_services/admin/settings/settings.service';

import { AddSubscriptionModalComponent } from './add-subscription-modal/add-subscription-modal.component';
import { ViewSubscriptionModalComponent } from './view-subscription-modal/view-subscription-modal.component';
import { ViewSubscriptionPlanModalComponent } from './view-subscription-plan-modal/view-subscription-plan-modal.component';
import { AdminService } from '../../../Utils/_services/admin/admin/admin.service';
import { ISubscription, SubscriptionPlan, SubscriptionStatus } from '../../../Utils/_models/subscription.model';
import { SubscriptionService } from '../../../Utils/_services/admin/subscription/subscription.service';

@Component({
  selector: 'app-subscription-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SpinnerLoaderComponent,
    AddSubscriptionModalComponent,
    ViewSubscriptionModalComponent,
    ViewSubscriptionPlanModalComponent,
    SearchBoxComponent,
  ],
  templateUrl: './subscription-management.component.html',
  styleUrl: './subscription-management.component.css',
})
export class SubscriptionManagementComponent implements OnInit {
  subscriptions = signal<ISubscription[]>([]);
  totalCount = signal(0);
  loader = signal(false);

  /** Map of subscriptionType (lowercase) → features array from the plans API */
  private planFeaturesMap: Record<string, string[]> = {};

  activeTab = signal<'subscriptions' | 'plans'>('subscriptions');
  plansList = signal<any[]>([]);
  showAddPlanModal = signal(false);
  showViewPlanModal = signal(false);
  selectedPlanDetails = signal<any>(null);

  currentPage = 1;
  pageSize = 5;
  pageSizes: number[] = [5, 10, 20, 50];
  totalPages = 0;
  startIndex = 1;
  endIndex = 0;

  searchTerm = '';

  plans: SubscriptionPlan[] = [];
  statuses: SubscriptionStatus[] = [];

  showAddModal = signal(false);
  showViewModal = signal(false);
  selectedSubscription = signal<ISubscription | null>(null);

  subscriptionType: string | null = null;
  subscriptionStatusFilter: 'active' | 'cancelled' | 'expired' | 'free' | null = null;

  selectedSubIds = signal<Set<string>>(new Set());
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);
  originalSubscriptions = signal<ISubscription[]>([]);

  planSortField = signal<string | null>(null);
  planSortOrder = signal<'asc' | 'desc' | null>(null);
  originalPlansList = signal<any[]>([]);
  allSelected = computed(() => {
    const subs = this.subscriptions();
    return subs.length > 0 && subs.every(s => this.selectedSubIds().has(s.id));
  });
  exportCount = computed(() => this.selectedSubIds().size);
  constructor(
    private subscriptionService: SubscriptionService,
    private settingService: SettingService,
    private adminService: AdminService
  ) { }

  ngOnInit(): void {
    this.plans = this.subscriptionService.getPlans();
    this.statuses = this.subscriptionService.getStatuses();
    // Load plan features first, then user data
    this.settingService.getAll().subscribe({
      next: (res: any) => {
        const plansList: any[] = res?.data?.plans ?? [];
        this.plansList.set(plansList);
        this.originalPlansList.set([...plansList]);
        this.planFeaturesMap = {};
        plansList.forEach((p: any) => {
          if (p.subscriptionType) {
            this.planFeaturesMap[p.subscriptionType.toLowerCase()] = p.features ?? [];
          }
        });
        if (this.planSortField() && this.planSortOrder()) {
          this.sortPlansLocal();
        }
      },
      error: () => {},
    });
    this.loadData();
  }

  loadData(): void {
    this.loader.set(true);
    const req: any = {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchTerm || null,
      subscriptionType: this.subscriptionType,
      subscriptionStatus: this.subscriptionStatusFilter,
    };

    this.adminService
      .getAllUsers(req)
      .pipe(finalize(() => this.loader.set(false)))
      .subscribe({
        next: (res: any) => {
          const apiData = res.data || {};
          const mappedSubscriptions: ISubscription[] = (apiData.users || []).map((user: any) => {
            const firstName = user.firstName || '';
            const lastName = user.lastName || '';
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

            const stripe = user.stripeSubscription;
            const amountCents = user.subscriptionAmountCents;
            const amount = typeof amountCents === 'number' ? amountCents / 100 : 0;
            const startDate = stripe?.currentPeriodStart
              ? new Date(stripe.currentPeriodStart).toLocaleDateString()
              : (user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A');
            const nextBillingDate = stripe?.currentPeriodEnd
              ? new Date(stripe.currentPeriodEnd).toLocaleDateString()
              : 'N/A';
            const stripeStatus = stripe?.status;
            const status = stripeStatus
              ? stripeStatus.charAt(0).toUpperCase() + stripeStatus.slice(1)
              : (user.subscriptionType === 'free' ? 'Active' : (user.isSubscriptionActive ? 'Active' : 'Inactive'));

            const isActive = user.subscriptionType === 'free' ? true : (stripe?.isActive ?? user.isSubscriptionActive ?? false);

            return {
              id: user._id || user.id,
              user: {
                name: `${firstName} ${lastName}`.trim(),
                email: user.email || '',
                avatarBg: '#ccc', // default color
                initials: initials || 'NA',
                avatar: user.profilePhotos?.find((p: any) => p.isPrimary)?.signedUrl,
              },
              plan: user.subscriptionType
                ? user.subscriptionType.charAt(0).toUpperCase() + user.subscriptionType.slice(1)
                : 'Free',
              status,
              date: startDate,
              amount,
              isActive,
              billingCycle: 'Monthly',
              nextBillingDate,
              features: this.planFeaturesMap[user.subscriptionType?.toLowerCase()] ?? [],
            };
          });

          this.subscriptions.set(mappedSubscriptions);
          this.originalSubscriptions.set([...mappedSubscriptions]);
          this.totalCount.set(apiData.totalCount || 0);
          this.selectedSubIds.set(new Set());
          if (this.currentSortField() && this.currentSortOrder()) {
            this.sortSubscriptionsLocal();
          } else {
            this.updatePagination();
          }
        },
        error: (err) => {
          console.error(err);
          this.subscriptions.set([]);
          this.originalSubscriptions.set([]);
          this.totalCount.set(0);
          this.selectedSubIds.set(new Set());
          this.updatePagination();
        }
      });
  }

  switchTab(tab: 'subscriptions' | 'plans'): void {
    this.activeTab.set(tab);
    if (tab === 'plans') {
      this.loadPlans();
    } else {
      this.loadData();
    }
  }

  loadPlans(): void {
    this.loader.set(true);
    this.settingService
      .getAll()
      .pipe(finalize(() => this.loader.set(false)))
      .subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.plans) {
            const plans = res.data.plans;
            this.plansList.set(plans);
            this.originalPlansList.set([...plans]);
            if (this.planSortField() && this.planSortOrder()) {
              this.sortPlansLocal();
            }
          } else {
            this.plansList.set([]);
            this.originalPlansList.set([]);
          }
        },
        error: err => {
          console.error(err);
          this.plansList.set([]);
          this.originalPlansList.set([]);
        },
      });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadData();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.subscriptionType = null;
    this.subscriptionStatusFilter = null;
    this.currentPage = 1;
    this.loadData();
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.currentPage = 1;
    this.loadData();
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize);
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalCount());
  }

  onPageSizeChange(newSize: any): void {
    this.pageSize = Number(newSize);
    this.currentPage = 1;
    this.loadData();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadData();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadData();
    }
  }

  openAddModal(): void {
    this.showAddModal.set(true);
  }

  closeAddModal(): void {
    this.showAddModal.set(false);
  }

  onSubscriptionAdded(sub: ISubscription): void {
    this.showAddModal.set(false);
    this.currentPage = 1;
    this.loadData();
  }

  viewSubscription(sub: ISubscription): void {
    console.log("viewSubscription triggered with:", sub);
    this.selectedSubscription.set(sub);
    this.showViewModal.set(true);
  }

  closeViewModal(): void {
    this.showViewModal.set(false);
    this.selectedSubscription.set(null);
  }

  onPlanAdded(plan: any): void {
    this.showAddPlanModal.set(false);
    this.loadPlans();
  }

  viewPlan(plan: any): void {
    console.log("viewPlan triggered with:", plan);
    this.selectedPlanDetails.set(plan);
    this.showViewPlanModal.set(true);
  }

  closeViewPlanModal(): void {
    this.showViewPlanModal.set(false);
    this.selectedPlanDetails.set(null);
  }

  cancelSubscription(sub: ISubscription): void {
    if (sub.status === 'Cancelled') return;
    this.subscriptionService.cancel(sub.id).subscribe(() => {
      this.loadData();
    });
  }

  toggleSelectAll(): void {
    const selected = new Set(this.selectedSubIds());
    if (this.allSelected()) {
      // Deselect all on current page
      this.subscriptions().forEach(s => selected.delete(s.id));
    } else {
      // Select all on current page
      this.subscriptions().forEach(s => selected.add(s.id));
    }
    this.selectedSubIds.set(selected);
  }

  toggleSelectSub(subId: string): void {
    const selected = new Set(this.selectedSubIds());
    if (selected.has(subId)) {
      selected.delete(subId);
    } else {
      selected.add(subId);
    }
    this.selectedSubIds.set(selected);
  }

  exportData(): void {
    const selectedIds = this.selectedSubIds();
    const subsToExport = this.subscriptions().filter(s => selectedIds.has(s.id));
    if (subsToExport.length === 0) return;

    const rows = [
      ['ID', 'Name', 'Email', 'Plan', 'Status', 'Date', 'Amount', 'Billing Cycle'],
      ...subsToExport.map(s => [
        s.id, s.user.name, s.user.email, s.plan, s.status, s.date, s.amount, s.billingCycle,
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscriptions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  getPlanClass(plan: SubscriptionPlan): string {
    const map: Record<SubscriptionPlan, string> = {
      'Pro Plan': 'plan-pro',
      'Enterprise': 'plan-basic',
      'Basic Plan': 'plan-vip',
      'Free': 'plan-free',
    };
    return map[plan] ?? '';
  }

  getStatusClass(status: SubscriptionStatus | string): string {
    const map: Record<string, string> = {
      Active: 'status-active',
      Inactive: 'status-expired',
      Cancelled: 'status-cancelled',
      Expired: 'status-expired',
      Pending: 'status-pending',
      Free: 'status-active',
    };
    return map[status] ?? '';
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortSubscriptionsLocal();
  }

  sortSubscriptionsLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.subscriptions.set([...this.originalSubscriptions()]);
      this.updatePagination();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.subscriptions()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'user') {
        valA = (a.user?.name || '').toLowerCase();
        valB = (b.user?.name || '').toLowerCase();
      } else if (field === 'plan') {
        valA = (a.plan || '').toLowerCase();
        valB = (b.plan || '').toLowerCase();
      } else if (field === 'status') {
        valA = a.isActive ? 1 : 0;
        valB = b.isActive ? 1 : 0;
      } else if (field === 'date') {
        const parseDate = (dStr: string) => {
          if (!dStr || dStr === 'N/A') return 0;
          const parts = dStr.split('/');
          if (parts.length === 3) {
            // MM/DD/YYYY
            return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1])).getTime();
          }
          return new Date(dStr).getTime();
        };
        valA = parseDate(a.date);
        valB = parseDate(b.date);
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.subscriptions.set(sorted);
    this.updatePagination();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  onSortPlans(field: string): void {
    if (this.planSortField() === field) {
      if (this.planSortOrder() === "asc") {
        this.planSortOrder.set("desc");
      } else if (this.planSortOrder() === "desc") {
        this.planSortOrder.set(null);
        this.planSortField.set(null);
      }
    } else {
      this.planSortField.set(field);
      this.planSortOrder.set("asc");
    }
    this.sortPlansLocal();
  }

  sortPlansLocal(): void {
    const field = this.planSortField();
    const order = this.planSortOrder();

    if (!field || !order) {
      this.plansList.set([...this.originalPlansList()]);
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.plansList()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (field === 'appStoreProductId') {
        valA = (a.appStoreProductId || '').toLowerCase();
        valB = (b.appStoreProductId || '').toLowerCase();
      } else if (field === 'subscriptionType') {
        valA = (a.subscriptionType || '').toLowerCase();
        valB = (b.subscriptionType || '').toLowerCase();
      } else if (field === 'duration') {
        valA = Number(a.duration || 0);
        valB = Number(b.duration || 0);
      } else if (field === 'price') {
        valA = Number(a.price || 0);
        valB = Number(b.price || 0);
      } else if (field === 'isActive') {
        valA = a.isActive ? 1 : 0;
        valB = b.isActive ? 1 : 0;
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.plansList.set(sorted);
  }

  getSortPlanIcon(field: string): string {
    if (this.planSortField() !== field) return "⇅";
    if (this.planSortOrder() === "asc") return "↑";
    if (this.planSortOrder() === "desc") return "↓";
    return "⇅";
  }
}
